<template>
  <div id="playlists">
    <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-purple header-enhanced">
      <!-- Playlist Statistics Cards -->
      <b-row class="mb-4">
        <b-col xl="3" md="6">
          <stats-card
            title="Total Playlists"
            type="gradient-purple"
            :sub-title="stats.totalPlaylists ? stats.totalPlaylists.toLocaleString() : 'Loading...'"
            icon="ni ni-music-note"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-purple mr-2">↗ {{ stats.playlistGrowth }}%</span>
              <span class="text-nowrap">vs last month</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Published"
            type="gradient-success"
            :sub-title="stats.publishedPlaylists ? stats.publishedPlaylists.toLocaleString() : 'Loading...'"
            icon="ni ni-check-bold"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-success mr-2">● Active</span>
              <span class="text-nowrap">Live playlists</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Premium"
            type="gradient-warning"
            :sub-title="stats.premiumPlaylists ? stats.premiumPlaylists.toLocaleString() : 'Loading...'"
            icon="ni ni-trophy"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-warning mr-2">👑 Paid</span>
              <span class="text-nowrap">Premium content</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Avg Songs"
            type="gradient-info"
            :sub-title="stats.avgSongsPerPlaylist ? stats.avgSongsPerPlaylist.toFixed(1) : 'Loading...'"
            icon="ni ni-note-03"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-info mr-2">🎵 Tracks</span>
              <span class="text-nowrap">Per playlist</span>
            </template>
          </stats-card>
        </b-col>
      </b-row>

      <!-- Page Title -->
      <b-row align-v="center" class="mb-3">
        <b-col>
          <h3 class="text-white mb-0">
            <i class="ni ni-music-note mr-2"></i>Playlist Management
          </h3>
          <p class="text-white-50 mb-0">Create and manage your music playlists</p>
        </b-col>
        <!-- <b-col class="text-right">
          <b-button
            variant="light"
            size="sm"
            class="shadow-sm"
            @click="openCreatePlaylist">
            <i class="ni ni-fat-add mr-1"></i>Create Playlist
          </b-button>
        </b-col> -->
      </b-row>
    </base-header>

    <div class="container-fluid mt--7">
      <!-- Enhanced Action Bar -->
      <b-card class="mb-4 shadow-sm action-bar-card">
        <b-row align-v="center">
          <b-col>
            <h5 class="mb-0 text-dark">
              <i class="ni ni-bullet-list-67 mr-2 text-primary"></i>
              All Playlists
            </h5>
            <small class="text-muted">Manage playlist ordering and visibility</small>
          </b-col>
          <b-col class="text-right">
            <b-button
              type="button"
              title="Create New Playlist"
              variant="primary"
              size="lg"
              class="create-playlist-btn"
              @click="openCreatePlaylist">
              <i class="ni ni-fat-add mr-2"></i>Create New Playlist
            </b-button>
          </b-col>
        </b-row>
      </b-card>

      <Vuetable
        data-path="playlists"
        pagination-path="pagination"
        ref="vuetable"
        track-by="playlistId"
        :api-url="apiUrl"
        :fields="fields"
        :http-options="httpOptions"
        :css="css.table"
        :per-page="10"
        :append-params="moreParams"
        :initial-page="initialPage"
        :query-params="{
          sort    : 'sort',
          page    : 'page',
          perPage : 'perPage'
        }"
        @vuetable:pagination-data="onPaginationData"
        @vuetable:loading="showLoader"
        @vuetable:loaded="hideLoader">

        <template
          class="text-center"
          slot="order"
          slot-scope="props">
          <select
            class="form-control"
            v-model="props.rowData.orderNo"
            @change="changeOrder(props.rowData)">
            <option
              v-for="(value, key) in orderOptions"
              :key="key"
              :value="value">
              {{ value }}
            </option>
          </select>
        </template>
        <template
          class="text-center"
          slot="bannerOrder"
          slot-scope="props">
          <select
            class="form-control"
            v-bind:class="getClass(props.rowData)"
            v-model="props.rowData.bannerOrderNo"
            @change="changeBannerOrder(props.rowData)">
            <option
              v-for="(value, key) in maxBanner"
              :key="key"
              :value="value">
              {{ value }}

            </option>
          </select>
        </template>

        <!-- Action Buttons -->
        <template
          class="text-center"
          slot="actions"
          slot-scope="props">
          <router-link :to="`/admin/playlist/view/${props.rowData.playlistId}`">
            <b-button
              class="m-1"
              title="View Playlist"
              type="button"
              size="sm"
              variant="primary">
              <b-icon icon="eye" aria-hidden="true"></b-icon>
            </b-button>
          </router-link>
          <b-button
            class="m-1"
            title="Delete Playlist"
            type="button"
            size="sm"
            variant="danger"
            @click="deletePlaylist(props.rowData)">
            <b-icon icon="trash" aria-hidden="true"></b-icon>
          </b-button>
        </template>
      </Vuetable>

      <!-- Enhanced Pagination -->
      <b-card class="mt-4 shadow-sm">
        <b-row align-v="center">
          <b-col md="6">
            <div class="pagination-info">
              <vuetable-pagination-info ref="paginationInfo" class="mb-0 text-muted"></vuetable-pagination-info>
            </div>
          </b-col>
          <b-col md="6" class="text-right">
            <div class="pagination-controls">
              <vuetable-pagination
                ref="pagination"
                @vuetable-pagination:change-page="onChangePage"
                :css="css.pagination"
              ></vuetable-pagination>
            </div>
          </b-col>
        </b-row>
      </b-card>
    </div>

    <!-- Modal -->
    <modals-container />

    <!-- Dialog -->
    <v-dialog />
    
    <AppLoaderAdmin :isLoading="loading" />
  </div>
</template>

<script>
  import Vue from 'vue';
  import moment from 'moment-timezone';
  import TableStyle from '../table-bootstrap-css.js';
  import baseHeader from '../../BaseHeader';
  import StatsCard from '../../Cards/StatsCard.vue';
  const CreatePlaylistModal = () => import('./CreatePlaylistModal');
  const Thumbnail = () => import('./Thumbnail');

  Vue.component('thumbnail', Thumbnail);

  export default {
    head : {
      title : {
        inner      : 'Playlist',
        separator  : '|',
        complement : 'Healthy Hiphop',
      },
    },
    data() {
      return {
        loading     : false,
        apiUrl      : process.env.VUE_APP_ROOT_API + 'api/playlists',
        moreParams  : {},
        css         : TableStyle,
        httpOptions : {
          headers : {
            'Authorization' : `Bearer ${this.$store.state.auth.accessToken}`,
          },
        },
        stats: {
          totalPlaylists: null,
          publishedPlaylists: null,
          premiumPlaylists: null,
          avgSongsPerPlaylist: null,
          playlistGrowth: 0,
        },
        fields : [ {
          name       : '__slot:order',
          title      : 'Playlist Order',
          sortField  : 'order_no',
          direction  : 'asc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : '__slot:bannerOrder',
          title      : 'Banner order',
          sortField  : 'banner_order_no',
          direction  : 'asc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : 'playlistId',
          title      : 'ID',
          sortField  : 'music_playlist_id',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : '__component:thumbnail',
          title      : 'Thumbnail',
          titleClass : 'text-center thumbnail',
          dataClass  : 'text-center',
        }, {
          name      : 'name',
          title     : 'Name',
          sortField : 'name',
          direction : 'desc',
        }, {
          name       : 'premium',
          title      : 'Premium',
          sortField  : 'premium',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : 'published',
          title      : 'Published',
          sortField  : 'published',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : 'created',
          title      : 'Created',
          sortField  : 'created',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : '__slot:actions',
          title      : 'Actions',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        } ],
        maxOrder     : 0,
        orderOptions : 0,
        initialPage  : parseInt(this.$route.params.page),
        maxBanner    : 0,
      }
    },
    components : {
      'AppLoaderAdmin'     : () => import('@/components/layout/AppLoaderAdmin'),
      'Vuetable'           : () => import('vuetable-2/src/components/Vuetable'),
      'VuetablePagination' :
        () => import('vuetable-2/src/components/VuetablePagination'),
      'VuetablePaginationInfo' :
        () => import('vuetable-2/src/components/VuetablePaginationInfo'),
        baseHeader,
        StatsCard
    },

    watch : {
      '$route.params.page'(val) {
        const currPage = parseInt(this.$refs.vuetable.currentPage);
        if (val && currPage !== parseInt(val)) {
          this.$nextTick(() => {
            this.$refs.vuetable.changePage(val);
          });
        }
      },
    },
    methods : {

      /**
       * Table Data
       * @param data
       * @return {object}
       */
      transform(data) {
        this.response = data;
        data = data.data;
        this.orderOptions = data.total;
        this.maxBanner = (this.response.maxBannerNumber && this.response.maxBannerNumber.banner_order_no) ? this.response.maxBannerNumber.banner_order_no : 1;
        const transformed = {};
        transformed.playlists = [];

        transformed.pagination = {
          'total'         : data.total,
          'per_page'      : data.perPage,
          'current_page'  : data.currentPage,
          'last_page'     : data.lastPage,
          'next_page_url' : data.nextPageUrl,
          'prev_page_url' : data.prevPageUrl,
          'from'          : data.from,
          'to'            : data.to,
        };

        for (let i = 0; i < data.data.length; i++) {
          const created = moment.utc(data.data[i].created);
          const createdConv = moment(created).local().format('MM/DD/YYYY');
          transformed.playlists.push({
            orderNo       : data.data[i].order_no,
            playlistId    : data.data[i].music_playlist_id,
            thumbnail     : data.data[i].thumbnail_url,
            name          : data.data[i].name,
            premium       : (data.data[i].premium === 1) ? 'Yes' : 'No',
            published     : (data.data[i].published === 1) ? 'Yes' : 'No',
            created       : createdConv,
            displayBanner : data.data[i].display_banner,
            bannerOrderNo : data.data[i].banner_order_no,
          });

          if (data.data[i].order_no > this.maxOrder)
            this.maxOrder = data.data[i].order_no + 1;
        }

        return transformed;
      },

      /**
       * Create New Playlist
       */
      openCreatePlaylist() {
        this.$modal.show(CreatePlaylistModal, {}, {
          height   : 'auto',
          adaptive : true,
        }, {
          'reload-table' : () => {
            this.reloadTable();
          },
        });
      },

      /**
       * Delete Playlist
       * @param data
       */
      deletePlaylist(data) {
        this.$modal.show('dialog', {
          title : 'Delete Playlist',
          text  : `Are you sure you want to delete <b>${data.name}</b>` +
            ` playlist?`,
          buttons : [ {
            title   : 'Yes',
            handler : () => {
              this.showLoader();
              this.$http.delete('api/playlist', {
                data : {
                  plistId : data.playlistId,
                },
              }).then(() => {
                this.$notify({
                  group : 'notif',
                  type  : 'success',
                  title : 'Success!',
                  text  : 'Deleted playlist successfully.',
                });

                if (this.$refs.vuetable.tableData.length === 1)
                  this.refreshTable();
                else
                  this.reloadTable();
                this.$modal.hide('dialog');
              }).catch(() => {
                this.$notify({
                  group : 'notif',
                  type  : 'error',
                  title : 'Failed',
                  text  : 'Oops! Something went wrong!',
                });
              }).finally(() => {
                this.hideLoader();
              });
            },
          }, {
            title : 'No',
          } ],
        })
      },

      /**
       * Refresh Playlist Table
       */
      refreshTable() {
        this.$nextTick(() => {
          this.$refs.vuetable.refresh();
        });
      },

      /**
       * Reload Playlist Table (current page will not reset)
       */
      reloadTable() {
        this.$nextTick(() => {
          this.$refs.vuetable.reload();
        });
      },

      /**
       * Update Paginaton info
       * @param paginationData
       */
      onPaginationData(paginationData) {
        this.$nextTick(() => {
          this.$refs.pagination.setPaginationData(paginationData);
          this.$refs.paginationInfo.setPaginationData(paginationData);
        });
      },

      /**
       * Update Vuetable page
       * @param page
       */
      onChangePage(page) {
        this.$nextTick(() => {
          this.$refs.vuetable.changePage(page);
          this.$router.replace('/admin/playlist/' + page);
        });
      },

      /**
       * Show Loader
       */
      showLoader() {
        this.loading = true;
      },

      /**
       * Hide Loader
       */
      hideLoader() {
        this.loading = false;
      },

      /**
       * Set Alternative Thumbnail
       * @param event
       */
      setAltThumbnail(event) {
        event.target.src =
          require('../../../assets/images/music-album-placeholder.png');
      },

      /**
       * Change Playlist Order
       * @param data
       */
      async changeOrder(data) {
        this.showLoader();
        await this.$http.post('api/playlist/order', {
          mPlaylistId : data.playlistId,
          orderNo     : data.orderNo,
        }).then(() => {
          this.refreshTable();
          this.$notify({
            group : 'notif',
            type  : 'success',
            title : 'Success!',
            text  : 'Updated playlist order successfully.',
          });
        }).catch(() => {
          this.$notify({
            group : 'notif',
            type  : 'error',
            title : 'Failed',
            text  : 'Oops! Something went wrong!',
          });
        }).finally(() => {
          this.hideLoader();
        });
      },
      async changeBannerOrder(data) {
        this.showLoader();
        await this.$http.post('api/playlist/bannerOrder', {
          mPlaylistId   : data.playlistId,
          bannerOrderNo : data.bannerOrderNo,
        }).then(() => {
          this.refreshTable();
          this.$notify({
            group : 'notif',
            type  : 'success',
            title : 'Success!',
            text  : 'Updated banner order successfully.',
          });
        }).catch(() => {
          this.$notify({
            group : 'notif',
            type  : 'error',
            title : 'Failed',
            text  : 'Oops! Something went wrong!',
          });
        }).finally(() => {
          this.hideLoader();
        });
      },

      getClass(data) {
        if(data.displayBanner == 0)
          return 'disabled-select';
        return 'enabled';
      },

      /**
       * Initialize Playlist Statistics
       */
      initializeStats() {
        // Simulated playlist statistics - replace with actual API calls
        this.stats.totalPlaylists = 89;
        this.stats.publishedPlaylists = 76;
        this.stats.premiumPlaylists = 23;
        this.stats.avgSongsPerPlaylist = 12.5;
        this.stats.playlistGrowth = 18.7;
      },
    },
    mounted() {
      this.initializeStats();
      this.$events.$on('filter-set', eventData => this.onFilterSet(eventData));
      this.$events.$on('filter-reset', () => this.onFilterReset());
      this.$events.$on('loading-state',
                       eventData => {
                         if (eventData)
                           this.showLoader();
                         else
                           this.hideLoader();
                       });
      this.$events.$on('reload', () => {
        this.reloadTable();
      });
    },
  }
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/settings";

/* Enhanced Header Styles */
.baseHeader {
  margin-top: -80px;
  position: relative;
  overflow: hidden;
}

.baseHeader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(138, 43, 226, 0.9) 0%, rgba(106, 13, 173, 0.9) 100%);
  z-index: 1;
}

.baseHeader::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(155, 89, 182, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(138, 43, 226, 0.2) 0%, transparent 50%);
  z-index: 1;
}

.header-enhanced .card {
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.header-enhanced .text-white {
  color: #fff !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-enhanced .card-body {
  position: relative;
  z-index: 2;
}

/* Stats Cards Hover Effects */
.stats-card-hover {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px !important;
}

.stats-card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

.stats-card-hover .card-body {
  transition: all 0.3s ease;
}

.stats-card-hover:hover .card-body {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
}

/* Enhanced Action Bar */
.action-bar-card {
  border-radius: 12px !important;
  border: none !important;
  transition: all 0.3s ease;
}

.action-bar-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
}

.create-playlist-btn {
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(0, 123, 255, 0.3);
}

.create-playlist-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(0, 123, 255, 0.4);
}

/* Enhanced Table Styles */
table {
  z-index: 99;
  position: relative;
  background: white;
  border-radius: 12px !important;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
}

.vuetable th {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
  border-bottom: 2px solid #dee2e6 !important;
  font-weight: 600 !important;
  color: #495057 !important;
  padding: 12px 8px !important;
}

.vuetable td {
  padding: 12px 8px !important;
  border-bottom: 1px solid #f1f3f4 !important;
  vertical-align: middle !important;
}

.vuetable tbody tr:hover {
  background-color: rgba(138, 43, 226, 0.05) !important;
  transform: scale(1.01);
  transition: all 0.2s ease;
}

/* Enhanced Form Controls */
.form-control {
  padding: 0.375rem 0.75rem;
  border-radius: 6px;
  border: 2px solid rgba(138, 43, 226, 0.2);
  transition: all 0.3s ease;
}

.form-control:focus {
  border-color: #8a2be2;
  box-shadow: 0 0 0 0.2rem rgba(138, 43, 226, 0.25);
}

/* Enhanced Pagination */
.pagination-info {
  padding: 16px 20px;
  background: rgba(248, 249, 250, 0.8);
  border-radius: 8px;
  font-weight: 500;
  color: #6c757d;
}

.pagination-controls {
  padding: 16px 20px;
}

/* Disabled Select Styles */
.disabled-select {
  opacity: 0.5;
  pointer-events: none;
  color: transparent;
  background: rgba(248, 249, 250, 0.5);
}

.header {
  margin-top: -80px;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.action-bar-card {
  animation: fadeInUp 0.8s ease-out;
}

table {
  animation: fadeInUp 0.8s ease-out 0.2s both;
}

/* Responsive Design */
@media (max-width: 768px) {
  .stats-card-hover:hover {
    transform: none;
  }

  .action-bar-card:hover {
    transform: none;
  }

  .create-playlist-btn:hover {
    transform: none;
  }

  .vuetable tbody tr:hover {
    transform: none;
  }

  .action-bar-card .row {
    text-align: center;
  }

  .create-playlist-btn {
    margin-top: 1rem;
    width: 100%;
  }
}
</style>